/**
 * CatCard USB protocol, host side.
 *
 * Written from the framing description in catcard's `docs/USB.md`:
 *
 *   byte 0   kind    0x01 START, 0x02 CONT
 *   byte 1   seq     increments per frame within a message, wrapping
 *   START:   2..4 u16 opcode (request) / status (reply), 4..8 u32 total length, 8..64 payload
 *   CONT:    2..64 payload
 */
import type { InstallProgress } from '../firmware/install';
import { Initiator, type Session } from './ncry';
import { Serial, TransportClosed, TransportTimeout, type Transport } from './transport';

export const CATCARD_VID = 0x39f2;
export const CATCARD_PID = 0x0401;

export const REPORT_LEN = 64;
export const KIND_START = 0x01;
export const KIND_CONT = 0x02;
export const START_PAYLOAD = REPORT_LEN - 8;
export const CONT_PAYLOAD = REPORT_LEN - 2;

export const Opcode = {
  Ping: 0x0001,
  Identify: 0x0002,
  UpgradeOffer: 0x0010,
  UpgradeCommit: 0x0011,
  ReadLog: 0x0012,
  UpgradePacked: 0x0013,
  InjectKey: 0x0020,
  UnlockPin: 0x0021,
  NcryMsg: 0x0041,
  PairCommit: 0x0042,
  PairReveal: 0x0043,
  PairConfirm: 0x0044,
  PairAbort: 0x0045,
} as const;
export type Opcode = (typeof Opcode)[keyof typeof Opcode];

export const Status = {
  Ok: 0x0000,
  UnknownOpcode: 0x0001,
  NotNow: 0x0002,
  BadRequest: 0x0003,
  Declined: 0x0004,
  Refused: 0x0005,
  Busy: 0x0006,
  RetryUncompressed: 0x0007,
} as const;

export const STATUS_NAMES: Record<number, string> = {
  0: 'Ok',
  1: 'UnknownOpcode',
  2: 'NotNow',
  3: 'BadRequest',
  4: 'Declined',
  5: 'Refused',
  6: 'Busy',
  7: 'RetryUncompressed',
};

export function statusName(status: number): string {
  return STATUS_NAMES[status] ?? `0x${status.toString(16).padStart(4, '0')}`;
}

/** Why an offered image was refused: the first byte of a Refused reply. */
export const REJECT_TEXT: Record<number, string> = {
  1: 'its length is wrong',
  2: 'it is too big for this board to stage',
  3: 'part of it arrived out of order',
  4: 'it ran past its declared end',
  5: 'it arrived incomplete',
  6: 'it is not a firmware image',
  7: 'its header is damaged',
  8: 'it is built for a different board',
  9: 'it is older than the device will accept',
  10: 'its signature does not verify',
  11: 'the device could not store it',
  12: 'this board has nowhere to stage an image',
  13: 'the staging memory is in use; finish what the device is doing first',
  14: 'the staging memory failed a read-back check',
  15: 'its compressed form could not be unpacked',
  16: 'its length is not aligned',
  17: 'the device is sealed against upgrades',
};

/** Deflate block size for a packed offer; the device checks it against its own slab. */
export const PACK_BLOCK = 8 * 1024;

async function deflateRaw(block: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream('deflate-raw');
  const out = new Response(new Blob([new Uint8Array(block)]).stream().pipeThrough(cs));
  return new Uint8Array(await out.arrayBuffer());
}

/**
 * The image as UpgradePacked wants it: `[u32 length][u32 block size]` then one raw
 * deflate stream per block. Nothing frames the streams; deflate marks its own end.
 * Null where the browser cannot deflate, or where packing does not make it smaller.
 */
export async function packImage(image: Uint8Array, block = PACK_BLOCK): Promise<Uint8Array | null> {
  if (typeof CompressionStream === 'undefined') return null;
  const parts: Uint8Array[] = [];
  const head = new Uint8Array(8);
  const v = new DataView(head.buffer);
  v.setUint32(0, image.length, true);
  v.setUint32(4, block, true);
  parts.push(head);
  for (let at = 0; at < image.length; at += block) parts.push(await deflateRaw(image.subarray(at, at + block)));
  const total = parts.reduce((n, p) => n + p.length, 0);
  if (total >= image.length) return null;
  const out = new Uint8Array(total);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

export interface OfferAccepted {
  /** The board accepts the key the image is signed with. */
  verified: boolean;
  length: number;
  version: string;
  keySlot: number;
  /** Older than the firmware running now: the device warns about it on its screen. */
  older: boolean;
}

export function parseOfferAccepted(body: Uint8Array): OfferAccepted {
  if (body.length < 22) throw new FramingError(`offer answer too short: ${body.length} bytes`);
  const v = new DataView(body.buffer, body.byteOffset, body.byteLength);
  const raw = body.subarray(13, 21);
  const nul = raw.indexOf(0);
  return {
    verified: body[0] !== 0,
    length: v.getUint32(1, true),
    version: dec.decode(raw.subarray(0, nul < 0 ? 8 : nul)),
    keySlot: body[21]!,
    older: (body[22] ?? 0) !== 0,
  };
}

export class OfferError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'OfferError';
  }
}

/** Device state bits in an Identify reply. */
export const state = {
  UNLOCKED: 1 << 0,
  BLANK: 1 << 1,
} as const;

/** Capability bits in an Identify reply. */
export const caps = {
  KEY_INJECTION: 1 << 0,
  UPGRADE: 1 << 1,
  DEBUG_MEM: 1 << 2,
  UNLOCK_PIN: 1 << 3,
  UPGRADE_PACKED: 1 << 4,
  /** Bit 5 was v1's unpaired channel: retired, never set by current firmware. */
  HOST_WALLET: 1 << 6,
  PAIRING: 1 << 7,
} as const;

export const CAP_NAMES: ReadonlyArray<{ bit: number; name: string; description: string; hazard?: boolean }> = [
  { bit: caps.UPGRADE, name: 'Upgrade', description: 'Can stage and install a firmware image over USB.' },
  { bit: caps.UPGRADE_PACKED, name: 'Packed upgrade', description: 'Accepts a deflated firmware image.' },
  { bit: caps.PAIRING, name: 'Pairing', description: 'Pairs an encrypted channel by a code compared on both screens.' },
  { bit: caps.HOST_WALLET, name: 'Addresses and signing', description: 'Shares addresses and signs transactions for a paired computer, if you approve on the device.' },
  { bit: caps.KEY_INJECTION, name: 'Key injection', description: 'Accepts keypresses over USB. Bench builds only.', hazard: true },
  { bit: caps.UNLOCK_PIN, name: 'PIN over USB', description: 'Accepts the login PIN over USB. Bench builds only.', hazard: true },
  { bit: caps.DEBUG_MEM, name: 'Memory monitor', description: 'Raw peek / poke / jsr. Must never ship.', hazard: true },
];

/** Bits in the flags byte of a ReadLog reply. */
export const LOG_WRAPPED = 1 << 0;

const enc = new TextEncoder();
const dec = new TextDecoder('utf-8', { fatal: false });

/** Split one message into 64-byte frames. */
export function encodeFrames(opcode: number, payload: Uint8Array = new Uint8Array(0)): Uint8Array[] {
  const frames: Uint8Array[] = [];
  let seq = 0;
  let n = Math.min(payload.length, START_PAYLOAD);
  const first = new Uint8Array(REPORT_LEN);
  const fv = new DataView(first.buffer);
  first[0] = KIND_START;
  first[1] = seq;
  fv.setUint16(2, opcode, true);
  fv.setUint32(4, payload.length, true);
  first.set(payload.subarray(0, n), 8);
  frames.push(first);
  let at = n;
  while (at < payload.length) {
    seq = (seq + 1) & 0xff;
    n = Math.min(payload.length - at, CONT_PAYLOAD);
    const f = new Uint8Array(REPORT_LEN);
    f[0] = KIND_CONT;
    f[1] = seq;
    f.set(payload.subarray(at, at + n), 2);
    frames.push(f);
    at += n;
  }
  return frames;
}

export interface Reply {
  status: number;
  body: Uint8Array;
}

export class FramingError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'FramingError';
  }
}

/**
 * Reassembles a reply from its frames. Feed it reports; it returns the reply once the
 * last frame is in. Empty reports (kind 0) are the device having nothing to say and are
 * ignored, not errors.
 */
export class ReplyDecoder {
  private status = 0;
  private total = 0;
  private body: Uint8Array | null = null;
  private got = 0;
  private seq = 0;

  feed(report: Uint8Array): Reply | null {
    const kind = report[0];
    if (kind !== KIND_START && kind !== KIND_CONT) return null;
    if (this.body === null) {
      if (kind !== KIND_START) throw new FramingError('continuation frame with no message open');
      const v = new DataView(report.buffer, report.byteOffset, report.byteLength);
      this.status = v.getUint16(2, true);
      this.total = v.getUint32(4, true);
      this.body = new Uint8Array(this.total);
      this.seq = report[1] ?? 0;
      const n = Math.min(this.total, START_PAYLOAD);
      this.body.set(report.subarray(8, 8 + n), 0);
      this.got = n;
    } else {
      if (kind !== KIND_CONT) throw new FramingError('a new message started while one was in progress');
      const expect = (this.seq + 1) & 0xff;
      if (report[1] !== expect) throw new FramingError(`frame sequence skipped: expected ${expect}, got ${report[1]}`);
      this.seq = expect;
      const n = Math.min(this.total - this.got, CONT_PAYLOAD);
      this.body.set(report.subarray(2, 2 + n), this.got);
      this.got += n;
    }
    if (this.got >= this.total) {
      const reply = { status: this.status, body: this.body };
      this.body = null;
      return reply;
    }
    return null;
  }
}

export interface Identify {
  protocol: number;
  unlocked: boolean;
  blank: boolean;
  caps: number;
  board: string;
  version: string;
}

/**
 * Identify reply: `[u16 protocol][u8 state][u8 caps][u8 len, board][u8 len, version]`.
 * The strings start at byte 4, after the capability byte.
 */
export function parseIdentify(body: Uint8Array): Identify {
  if (body.length < 4) throw new FramingError(`Identify reply too short: ${body.length} bytes`);
  const v = new DataView(body.buffer, body.byteOffset, body.byteLength);
  const protocol = v.getUint16(0, true);
  const st = body[2] ?? 0;
  const capBits = body[3] ?? 0;
  let at = 4;
  const strings: string[] = [];
  for (let i = 0; i < 2; i++) {
    const n = body[at];
    if (n === undefined) throw new FramingError('Identify reply is missing a string');
    strings.push(dec.decode(body.subarray(at + 1, at + 1 + n)));
    at += 1 + n;
  }
  return {
    protocol,
    unlocked: (st & state.UNLOCKED) !== 0,
    blank: (st & state.BLANK) !== 0,
    caps: capBits,
    board: strings[0] ?? '',
    version: strings[1] ?? '',
  };
}

export interface LogPage {
  total: number;
  wrapped: boolean;
  bytes: Uint8Array;
}

/** ReadLog reply: `[u32 total][u8 flags][bytes]`. */
export function parseLogPage(body: Uint8Array): LogPage {
  if (body.length < 5) throw new FramingError(`ReadLog reply too short: ${body.length} bytes`);
  const v = new DataView(body.buffer, body.byteOffset, body.byteLength);
  return {
    total: v.getUint32(0, true),
    wrapped: ((body[4] ?? 0) & LOG_WRAPPED) !== 0,
    bytes: body.subarray(5),
  };
}

export class CatCardError extends Error {
  constructor(
    readonly opcode: number,
    readonly status: number,
    readonly body: Uint8Array,
  ) {
    super(`device answered ${statusName(status)}`);
    this.name = 'CatCardError';
  }
}

export interface DeviceLog {
  text: string;
  total: number;
  wrapped: boolean;
}

export class CatCardClient {
  private readonly serial = new Serial();

  constructor(
    readonly transport: Transport,
    readonly timeoutMs = 3000,
  ) {}

  /** One request, one reply, whatever its status. */
  request(opcode: number, payload: Uint8Array = new Uint8Array(0), timeoutMs = this.timeoutMs): Promise<Reply> {
    return this.serial.run(async () => {
      this.transport.drain();
      for (const f of encodeFrames(opcode, payload)) await this.transport.write(f);
      const decoder = new ReplyDecoder();
      for (;;) {
        const reply = decoder.feed(await this.transport.read(timeoutMs));
        if (reply) return reply;
      }
    });
  }

  /** A request that must succeed; any other status becomes a `CatCardError`. */
  async call(opcode: number, payload?: Uint8Array, timeoutMs?: number): Promise<Uint8Array> {
    const r = await this.request(opcode, payload, timeoutMs);
    if (r.status !== Status.Ok) throw new CatCardError(opcode, r.status, r.body);
    return r.body;
  }

  /** Sends `payload` and returns the echo and the round trip in milliseconds. */
  async ping(payload: Uint8Array | string = 'hello'): Promise<{ echo: Uint8Array; ms: number }> {
    const bytes = typeof payload === 'string' ? enc.encode(payload) : payload;
    const t0 = performance.now();
    const echo = await this.call(Opcode.Ping, bytes);
    return { echo, ms: performance.now() - t0 };
  }

  async identify(): Promise<Identify> {
    return parseIdentify(await this.call(Opcode.Identify));
  }

  /**
   * Opens a pairing handshake: commit, then reveal. Returns the code both screens now
   * show, and the steps that finish or abandon it. Nothing is paired until a person has
   * compared the codes on both sides and both have said yes.
   */
  async startPairing(): Promise<PendingPairing> {
    const host = new Initiator();
    const c = await this.request(Opcode.PairCommit, host.commit);
    if (c.status === Status.NotNow) throw new PairingError('Unlock the CatCard with its PIN first.');
    if (c.status === Status.Busy) {
      throw new PairingError('A pairing code is on the CatCard, or a pairing was started a few seconds ago. Answer it or wait, then try again.');
    }
    if (c.status === Status.Refused) {
      // After several abandoned attempts the device blocks pairing until the person
      // dismisses its warning. Its reason says so.
      const reason = dec.decode(c.body).trim() || 'pairing is blocked';
      throw new PairingError(
        `The CatCard refused to pair: ${reason}. Several pairing attempts were started and dropped, which is what a relay in the middle would do. Dismiss the warning on the CatCard, check what the cable passes through, then try again.`,
      );
    }
    if (c.status !== Status.Ok || c.body.length !== 32) throw new PairingError(`The CatCard refused to pair (${statusName(c.status)}).`);
    const session = host.finish(c.body);
    const r = await this.request(Opcode.PairReveal, host.public);
    if (r.status !== Status.Ok) throw new PairingError(`The CatCard refused the pairing (${statusName(r.status)}).`);
    return new PendingPairing(this, session);
  }

  /**
   * Sends one upgrade offer and returns the device's answer. The device may answer on the
   * first frame (locked, or an earlier offer waiting), so replies are watched for while
   * the frames go out, and sending stops as soon as one arrives.
   */
  private sendOffer(opcode: number, payload: Uint8Array, onSent: (n: number) => void): Promise<Reply> {
    return this.serial.run(async () => {
      this.transport.drain();
      const decoder = new ReplyDecoder();
      const frames = encodeFrames(opcode, payload);
      for (let i = 0; i < frames.length; i++) {
        await this.transport.write(frames[i]!);
        if (i % 16 === 0 || i === frames.length - 1) {
          onSent(Math.min(payload.length, START_PAYLOAD + i * CONT_PAYLOAD));
          for (let r = this.transport.poll(); r; r = this.transport.poll()) {
            const reply = decoder.feed(r);
            if (reply) return reply;
          }
        }
      }
      // Checking the signature over a whole image takes the device a moment.
      for (;;) {
        const reply = decoder.feed(await this.transport.read(60_000));
        if (reply) return reply;
      }
    });
  }

  /**
   * Offers a raw signed image (not a .dfu) for installation, deflated when the device can
   * take that. Resolves once the device has checked it and put the question on its
   * screen; `awaitApproval` then waits for the person's answer.
   */
  async offerImage(image: Uint8Array, capBits: number, onProgress?: (p: InstallProgress) => void): Promise<OfferAccepted> {
    let reply: Reply | null = null;
    const report = (sent: number, total: number) =>
      onProgress?.({ stage: sent >= total ? 'inspect' : 'upload', sent: Math.min(sent, total), total });
    if (capBits & caps.UPGRADE_PACKED) {
      const packed = await packImage(image);
      if (packed) {
        reply = await this.sendOffer(Opcode.UpgradePacked, packed, (n) => report(n, packed.length));
        // Not a refusal of the image: the device has no room to inflate right now.
        if (reply.status === Status.RetryUncompressed) reply = null;
      }
    }
    reply ??= await this.sendOffer(Opcode.UpgradeOffer, image, (n) => report(n, image.length));
    if (reply.status === Status.Ok) return parseOfferAccepted(reply.body);
    if (reply.status === Status.NotNow) {
      throw new OfferError('The CatCard cannot take it now: it is locked, or an earlier offer is waiting for an answer on its screen. Answer that, or unlock it, then try again.');
    }
    if (reply.status === Status.Busy) throw new OfferError('The CatCard is busy. Finish what it is doing, then try again.');
    if (reply.status === Status.Refused) {
      const why = REJECT_TEXT[reply.body[0] ?? 0] ?? `reason ${reply.body[0] ?? 'unknown'}`;
      throw new OfferError(`The CatCard refused the image: ${why}.`);
    }
    throw new OfferError(`The CatCard answered ${statusName(reply.status)}.`);
  }

  /**
   * Waits for the person to answer the offer on the device. It restarts to install on a
   * yes, which from here looks like the device leaving the bus; a no arrives as Declined.
   */
  awaitApproval(waitMs = 15 * 60_000): Promise<'restarting' | 'declined'> {
    return this.serial.run(async () => {
      const decoder = new ReplyDecoder();
      const deadline = Date.now() + waitMs;
      for (;;) {
        let r: Uint8Array;
        try {
          r = await this.transport.read(Math.max(1, Math.min(30_000, deadline - Date.now())));
        } catch (err) {
          if (err instanceof TransportClosed) return 'restarting';
          if (err instanceof TransportTimeout && Date.now() < deadline) continue;
          throw new OfferError('Nobody answered on the CatCard. The offer is still on its screen until it is answered or unplugged.');
        }
        const reply = decoder.feed(r);
        if (reply?.status === Status.Declined) return 'declined';
      }
    });
  }

  /** One sealed command inside a session: the inner status and payload. */
  async sealed(session: Session, opcode: number, payload: Uint8Array = new Uint8Array(0)): Promise<Reply> {
    const plain = new Uint8Array(2 + payload.length);
    new DataView(plain.buffer).setUint16(0, opcode, true);
    plain.set(payload, 2);
    const r = await this.request(Opcode.NcryMsg, session.seal(plain));
    if (r.status !== Status.Ok) {
      throw new PairingError(
        r.status === Status.Declined ? 'Pairing was declined on the CatCard.' : `The encrypted session ended (${statusName(r.status)}). Pair again.`,
      );
    }
    const inner = session.open(r.body);
    if (inner.length < 2) throw new PairingError('The CatCard sent an empty sealed reply.');
    return { status: new DataView(inner.buffer, inner.byteOffset).getUint16(0, true), body: inner.subarray(2) };
  }

  /** Reads the whole boot/diagnostic log, one page per round trip. */
  async readLog(): Promise<DeviceLog> {
    const chunks: Uint8Array[] = [];
    let offset = 0;
    let total = 0;
    let wrapped = false;
    for (;;) {
      const req = new Uint8Array(4);
      new DataView(req.buffer).setUint32(0, offset, true);
      const page = parseLogPage(await this.call(Opcode.ReadLog, req));
      total = page.total;
      wrapped = page.wrapped;
      if (page.bytes.length === 0 || offset >= total) break;
      chunks.push(page.bytes);
      offset += page.bytes.length;
      if (offset >= total) break;
    }
    const all = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
    let at = 0;
    for (const c of chunks) {
      all.set(c, at);
      at += c.length;
    }
    return { text: dec.decode(all), total, wrapped };
  }
}

export class PairingError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'PairingError';
  }
}

/** A handshake whose code is on both screens, waiting for the two people to compare it. */
export class PendingPairing {
  constructor(
    private readonly client: CatCardClient,
    readonly session: Session,
  ) {}

  get code(): number {
    return this.session.code;
  }

  /**
   * The host's user said the codes match. Sends the sealed confirmation until the
   * device's user has answered too, and resolves once the channel is paired.
   */
  async confirm(opts: { signal?: AbortSignal; pollMs?: number; waitMs?: number } = {}): Promise<Session> {
    const deadline = Date.now() + (opts.waitMs ?? 130_000);
    for (;;) {
      const r = await this.client.sealed(this.session, Opcode.PairConfirm);
      if (r.status === Status.Ok) return this.session;
      if (r.status !== Status.NotNow) throw new PairingError(`The CatCard refused the pairing (${statusName(r.status)}).`);
      if (opts.signal?.aborted || Date.now() > deadline) {
        await this.abort();
        throw new PairingError(opts.signal?.aborted ? 'Pairing was cancelled.' : 'Nobody answered on the CatCard in time.');
      }
      await new Promise((res) => setTimeout(res, opts.pollMs ?? 500));
    }
  }

  /** The host's user said no, or gave up: takes the prompt off the device. */
  async abort(): Promise<void> {
    await this.client.request(Opcode.PairAbort).catch(() => undefined);
  }
}
